import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { auth } from '../lib/auth'
import { analytics } from '../lib/analytics'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface UserProfile {
  id: string
  email: string
  firstName: string | null
  lastName: string | null
  displayName: string | null
  username: string | null
  emailVerificationStatus: 'none' | 'sent' | 'verified'
  locale: string | null
}

interface AuthContextValue {
  isAuthenticated: boolean
  isLoading: boolean
  profile: UserProfile | null
  login: () => void
  logout: () => void
  // Called by AuthCallbackPage once WorkOS has redirected back and the API
  // has set the httpOnly access-token cookie. Resolves once the profile is
  // loaded; throws if the session doesn't actually check out.
  completeLogin: () => Promise<void>
}

// ── Helpers ───────────────────────────────────────────────────────────────────

export function getInitials(profile: UserProfile | null): string {
  if (!profile) return '?'

  const first = profile.firstName?.trim()
  const last = profile.lastName?.trim()
  if (first && last) return (first[0] + last[0]).toUpperCase()
  if (first) return first.slice(0, 2).toUpperCase()

  const display = profile.displayName?.trim()
  if (display) {
    const parts = display.split(' ').filter(Boolean)
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
    return display.slice(0, 2).toUpperCase()
  }

  const local = profile.email.split('@')[0]
  const emailParts = local.split(/[.\-_]/).filter(Boolean)
  if (emailParts.length >= 2) return (emailParts[0][0] + emailParts[1][0]).toUpperCase()
  return local.slice(0, 2).toUpperCase()
}

// ── Context ───────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [profile, setProfile] = useState<UserProfile | null>(null)

  // Asks the API "is there a valid session?" via the httpOnly cookie —
  // there is no local token to check, so this always has to be a network
  // call. One retry through /auth/refresh if the access token has expired
  // but the sealed session cookie is still good. Used both on mount (to
  // restore a session across a page reload — this is the fix for the
  // confirmed production blocker in GearLocker-Context.md Section 15) and
  // from AuthCallbackPage right after the WorkOS redirect.
  const checkSession = async (): Promise<boolean> => {
    try {
      const res = await fetch('/api/user/me', { credentials: 'include' })

      if (res.ok) {
        const { data } = await res.json()
        setProfile(data)
        setIsAuthenticated(true)
        auth.markAuthenticated()
        // No-ops if consent hasn't been granted / analytics never initialized.
        // Deliberately not passing email/name here — just the id, to keep
        // what reaches PostHog minimal. Add properties only if you decide
        // you need them for a specific dashboard.
        analytics.identify(data.id)
        return true
      }

      if (res.status === 401) {
        const refreshRes = await fetch('/api/auth/refresh?app=web', {
          method: 'POST',
          credentials: 'include',
        })
        if (refreshRes.ok) {
          const retry = await fetch('/api/user/me', { credentials: 'include' })
          if (retry.ok) {
            const { data } = await retry.json()
            setProfile(data)
            setIsAuthenticated(true)
            auth.markAuthenticated()
            analytics.identify(data.id)
            return true
          }
        }
      }
    } catch (err) {
      console.error('[auth] Session check failed:', err)
    }

    setProfile(null)
    setIsAuthenticated(false)
    auth.markLoggedOut()
    return false
  }

  useEffect(() => {
    checkSession().finally(() => setIsLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const login = () => {
    window.location.href = '/api/auth/login?app=web'
  }

  const logout = () => {
    auth.markLoggedOut()
    setIsAuthenticated(false)
    setProfile(null)
    analytics.resetIdentity()
    // Redirect through WorkOS to clear their session, then back to our /login
    window.location.href = '/api/auth/logout?app=web'
  }

  const completeLogin = async (): Promise<void> => {
    const ok = await checkSession()
    if (!ok) throw new Error('Session check failed after login')
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, profile, login, logout, completeLogin }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
